import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import submit from "submit-form";
import Drawer from "./drawer";
import { FoodItem } from "../views/athlete-portal/nutrition/meal-planner/shared/FoodItem";
import { MacrosDisplay } from "../views/athlete-portal/nutrition/meal-planner/shared/MacrosDisplay";
import {
  customRound,
  formatCustomUnitMeasurementEquivalenceSuffix,
  parseUnitToShow,
} from "../utils/nutrition-util";

const MealClipboardContent = ({ query, locals }) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [, setClearTrigger] = useState(0);

  const { hasClipboardData, mealsToShow } = (() => {
    if (typeof window === "undefined") return { hasClipboardData: false, mealsToShow: null };
    try {
      const m = localStorage.getItem("mealsToCopy");
      const meta = localStorage.getItem("mealCopyMetadata");
      const hasClipboardData = !!(m && meta);
      const mealsToShow = m ? JSON.parse(m) : null;
      return { hasClipboardData, mealsToShow };
    } catch {
      return { hasClipboardData: false, mealsToShow: null };
    }
  })();

  if (!hasClipboardData) return null;

  return (
    <div className="clipboard-content mobile-hidden">
      <button onClick={() => setOpen(true)}>
        <span className="clipboard-text">{t("clipboard")}</span>
        <img
          src="/img/new-icons/horizontal-clipboard-text.svg"
          alt="Paste Icon"
        />
      </button>
      <Drawer
        show={open}
        close={() => {
          setOpen(false);
        }}
        title={t("clipboard")}
      >
        <DrawerClipboardContent
          setOpen={setOpen}
          onClear={() => setClearTrigger((c) => c + 1)}
          mealsToCopy={mealsToShow}
          locals={locals}
        />
      </Drawer>
    </div>
  );
};
export default MealClipboardContent;

const DrawerClipboardContent = ({
  setOpen,
  onClear,
  mealsToCopy,
  locals,
}) => {
  const { t } = useTranslation("common");
  const { basicMealsInformation = [] } = locals || {};

  /**
   * Group foods by meal or single foods (same logic as MealPlannerCalendar)
   */
  const groupFoodsByMealId = (foods) => {
    if (!Array.isArray(foods)) return { grouped: [], singles: [] };

    const mealInfoMap = new Map(
      basicMealsInformation?.map((m) => [String(m._id), m]) || []
    );

    const groupedMap = new Map();
    const singles = [];

    for (const food of foods) {
      const mealId = food.mealId ? String(food.mealId) : null;

      // Food without mealId (it is single food)
      if (!mealId) {
        singles.push(food);
        continue;
      }

      // Check if mealId exists in basicMealsInformation
      const mealInfo = mealInfoMap.get(mealId);

      if (!mealInfo) {
        // Doesn't exist (it is single food)
        singles.push(food);
        continue;
      }

      // Group
      if (!groupedMap.has(mealId)) {
        groupedMap.set(mealId, {
          meal: mealInfo,
          foods: [],
          totals: {
            proteins: 0,
            carbs: 0,
            fats: 0,
            calories: 0,
          },
        });
      }

      const group = groupedMap.get(mealId);

      group.foods.push(food);

      // --- accumulate macros ---
      if (food.foodId) {
        const { proteins = 0, carbs = 0, fats = 0, calories = 0 } = food.foodId;

        group.totals.proteins += Number(proteins) || 0;
        group.totals.carbs += Number(carbs) || 0;
        group.totals.fats += Number(fats) || 0;
        group.totals.calories += Number(calories) || 0;
      }
    }

    return {
      grouped: [...groupedMap.values()],
      singles,
    };
  };

  const handleDiscard = () => {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem("mealsToCopy");
      localStorage.removeItem("mealCopyMetadata");
      setOpen(false);
      onClear?.();
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          mealsInClipboard: "null",
        },
      });
    }
  };

  if (!mealsToCopy || !Array.isArray(mealsToCopy) || mealsToCopy.length === 0) {
    return null;
  }

  const { grouped, singles } = groupFoodsByMealId(mealsToCopy);

  // Calculate total macros from all foods (grouped + singles)
  let totalProteins = 0;
  let totalCarbs = 0;
  let totalFats = 0;
  let totalCalories = 0;

  // Sum macros from grouped meals
  grouped.forEach((group) => {
    totalProteins += Number(group.totals.proteins) || 0;
    totalCarbs += Number(group.totals.carbs) || 0;
    totalFats += Number(group.totals.fats) || 0;
    totalCalories += Number(group.totals.calories) || 0;
  });

  // Sum macros from single foods
  singles.forEach((food) => {
    if (food.foodId) {
      totalProteins += Number(food.foodId.proteins) || 0;
      totalCarbs += Number(food.foodId.carbs) || 0;
      totalFats += Number(food.foodId.fats) || 0;
      totalCalories += Number(food.foodId.calories) || 0;
    }
  });

  const hasMacrosTotal =
    totalProteins !== 0 ||
    totalCarbs !== 0 ||
    totalFats !== 0 ||
    totalCalories !== 0;

  return (
    <div className="clipboard-main-section">
      <div className="clipboard-body">
        <div className="chip info clipboard-chip">
          {t("pasteIntoMeal")}
        </div>
        <div className="meal-info-section">
          <section>
            {/* Render grouped meals */}
            {grouped.map((group) => {
              const { meal, foods: foodsOfMeal, totals } = group;
              const {
                name: mealname,
                description: mealDescription,
                _id: mealId,
              } = meal;
              const {
                calories = 0,
                carbs = 0,
                fats = 0,
                proteins = 0,
              } = totals;
              return (
                <div key={mealId} className="grouped-meal full-info">
                  {/* Meal Header */}
                  <span className="title">{mealname}</span>
                  <span className="subtitle pb-1.5">
                    {mealDescription}
                  </span>
                  {/* Foods inside this meal */}
                  {foodsOfMeal.map((food, index) => {
                    const {
                      type: foodType,
                      _id,
                      quantityNumber,
                      unitOfMeasure,
                      foodId,
                    } = food;

                    if (!foodId) return null;

                    const { name, quantityDescription, brand } = foodId;

                    return (
                      <div
                        className="meal-foods"
                        key={`${mealId}-${index}-${_id}`}
                      >
                        <span>
                          {brand &&
                          !name.toLowerCase().includes(brand.toLowerCase())
                            ? `${name} (${brand})`
                            : name}
                        </span>
                        <span>
                          {Number(quantityNumber) || 0}{" "}
                          {parseUnitToShow(
                            Number(quantityNumber) || 0,
                            foodType,
                            unitOfMeasure,
                            quantityDescription
                          )}
                          {formatCustomUnitMeasurementEquivalenceSuffix(
                            Number(quantityNumber) || 0,
                            unitOfMeasure,
                            foodId
                          )}
                        </span>
                      </div>
                    );
                  })}
                  <MacrosDisplay
                    proteins={Number(proteins) || 0}
                    carbs={Number(carbs) || 0}
                    fats={Number(fats) || 0}
                    calories={Number(calories) || 0}
                    className="values meal-values"
                  />
                </div>
              );
            })}
            {/* Render single foods */}
            {singles?.map((food, index) => {
              const {
                type: foodType,
                _id,
                quantityNumber,
                unitOfMeasure,
                foodId,
              } = food;
              if (!foodId) return null;

              const {
                calories = 0,
                carbs = 0,
                fats = 0,
                name,
                proteins = 0,
                quantityDescription,
                brand,
              } = foodId;

              return (
                <FoodItem
                  key={`${index}-${_id}`}
                  name={name}
                  brand={brand}
                  quantityNumber={Number(quantityNumber) || 0}
                  unitOfMeasure={unitOfMeasure}
                  quantityDescription={quantityDescription}
                  foodType={foodType}
                  proteins={Number(proteins) || 0}
                  carbs={Number(carbs) || 0}
                  fats={Number(fats) || 0}
                  calories={Number(calories) || 0}
                  food={food}
                  hideHeaderActions
                />
              );
            })}
            {/* Resume: Total macros */}
            {hasMacrosTotal && (
              <div className="resume">
                <div className="protein-container">{customRound(totalProteins)}</div>
                <div className="carbs-container">{customRound(totalCarbs)}</div>
                <div className="fat-container">{customRound(totalFats)}</div>
                <div className="calories-container">{customRound(totalCalories)}</div>
              </div>
            )}
          </section>
        </div>
      </div>
      <div className="clipboard-footer-content">
        <div className="clipboard-button-container">
          <button onClick={handleDiscard}>{t("discardCopy")}</button>
        </div>
      </div>
    </div>
  );
};

MealClipboardContent.propTypes = {
  query: PropTypes.string,
  locals: PropTypes.object,
};

DrawerClipboardContent.propTypes = {
  setOpen: PropTypes.func,
  onClear: PropTypes.func,
  mealsToCopy: PropTypes.array,
  locals: PropTypes.object,
};
